package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.appScope
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.asList
import org.w3c.dom.url.URL

/**
 * The attribute the server writes the cursor for the next page into, on both the anchor and the
 * fragment it fetches. Also the query parameter that carries it.
 */
private const val PAGE_TOKEN = "pageToken"

/**
 * Appends further pages of a venue's or a tag's events in place.
 *
 * The server renders the first page along with the rest of the page and, when there is more
 * behind it, a "more events" anchor pointing at the same page URL carrying the cursor. Followed
 * plainly — no JavaScript — that is ordinary pagination: the next page of events server-renders
 * in place of this one. This module takes the click instead, fetches the same page of events from
 * the `/eventlist` fragment endpoint, and appends it to what is already on screen.
 *
 * The DOM contract, written by `pagedEventList` on the server:
 *
 * - the module element carries `data-events-endpoint`, the fragment endpoint for this entity and
 *   date range, with no cursor on it;
 * - `[data-event-list]` wraps the events, and its single element child is the item container that
 *   further pages are appended into;
 * - `[data-load-more]` is the anchor, holding the cursor for the next page.
 *
 * The fragment answers with `[data-event-list-page]` around the same item container, carrying the
 * cursor for the page after it in `data-next-token` — absent once the list is exhausted.
 *
 * **A page that arrives empty does not mean the end of the list**, and the server already follows
 * the cursor past empty pages before answering, so a fragment that comes back with no events in
 * it and no next token really is the end.
 */
fun loadMoreEvents(element: Element) {
    val endpoint = element.getAttribute("data-events-endpoint") ?: return
    val anchor = element.querySelector("[data-load-more]") as? HTMLAnchorElement ?: return
    val list = element.querySelector("[data-event-list]")?.firstElementChild ?: return

    val label = anchor.textContent
    var loading = false

    // Deliberately not the `onClick` helper, which launches the handler in a coroutine: that
    // returns to the browser before the handler has run, so a preventDefault() inside it lands
    // after the event has already been dispatched and the anchor navigates anyway. Cancelling
    // the navigation has to happen synchronously; only the fetch is deferred.
    anchor.addEventListener("click", { domEvent ->
        domEvent.preventDefault()
        if (!loading) {
            val token = anchor.getAttribute("data-load-more")
            if (token != null) {
                loading = true
                anchor.textContent = "Loading…"
                appScope.launch {
                    loadPage(endpoint, token, list, anchor, label)
                    loading = false
                }
            }
        }
    })
}

/**
 * Fetch the page the anchor is holding the cursor for, append it, and move the anchor on to the
 * page after it — or take the anchor away once the list is exhausted.
 */
private suspend fun loadPage(
    endpoint: String,
    token: String,
    list: Element,
    anchor: HTMLAnchorElement,
    label: String?
) {
    val separator = if (endpoint.contains('?')) "&" else "?"
    val fragment = Account.load("$endpoint$separator$PAGE_TOKEN=${encodeURIComponent(token)}")

    if (fragment == null) {
        // What is already on screen is still worth reading, so a page that will not load leaves
        // the list standing and offers to try again.
        anchor.textContent = "Try again"
        return
    }

    val holder = document.createElement("div")
    holder.innerHTML = fragment

    // The class names in the fragment's stylesheet are content hashes, so adding it to the
    // document is safe however many times a reader asks for another page: a rule already present
    // is the same rule.
    holder.querySelectorAll("style").asList().forEach { document.head?.appendChild(it) }

    // A response that is not a page of events at all — the endpoint answered 404, say, which the
    // HTTP client hands back as a body rather than as a failure. Reported as a failed load
    // rather than as the end of the list, which is what dropping the control would say.
    val page = holder.querySelector("[data-event-list-page]")
    if (page == null) {
        anchor.textContent = "Try again"
        return
    }
    page.firstElementChild?.let { append(list, it) }

    val next = page.getAttribute("data-next-token")
    if (next == null) {
        anchor.parentElement?.remove()
    } else {
        anchor.setAttribute("data-load-more", next)
        anchor.href = withPageToken(anchor.href, next)
        anchor.textContent = label
    }
}

/**
 * Move the new page's items onto the end of the list.
 *
 * The dated list groups its events into a container per day, and a page boundary falls wherever
 * the cursor happens to land — routinely part-way through a day. Appending that day's second
 * container as it stands would draw its date heading a second time, so the two are merged.
 */
private fun append(list: Element, items: Element) {
    val dateAttribute = "data-vegasful-date-container"

    while (items.firstElementChild != null) {
        val incoming = items.firstElementChild!!
        val date = incoming.getAttribute(dateAttribute)
        val last = list.lastElementChild

        if (date != null && last?.getAttribute(dateAttribute) == date) {
            // Same day, continued: take its events rather than its heading.
            val target = last.lastElementChild ?: last
            val source = incoming.lastElementChild ?: incoming
            while (source.firstChild != null) {
                target.appendChild(source.firstChild!!)
            }
            incoming.remove()
        } else {
            list.appendChild(incoming)
        }
    }
}

/**
 * The same address with its cursor replaced, so that the anchor a reader without JavaScript would
 * have followed goes on naming the page this module will fetch next.
 */
private fun withPageToken(href: String, token: String): String {
    val url = URL(href)
    url.searchParams.set(PAGE_TOKEN, token)
    return url.pathname + url.search
}

